/**
 * Flow Type Visibility System
 *
 * Centralized system for controlling which flow types are available to users.
 * Supports three control mechanisms:
 * 1. ALWAYS_AVAILABLE - Available to all users in any environment
 * 2. GROWTHBOOK_ROLLOUT - Available via GrowthBook feature flags (for gradual rollout)
 * 3. DB_ORG_SPECIFIC - Available based on org database flags (features_enabled)
 *
 * Flow types can move between categories over time by updating the configuration below.
 */

import { ORG_FEATURE_FLAG_SETTINGS } from 'js/constants/config';
import { FlowType } from 'new/elements/flows/types';
import {
  FLOW_TYPE_STREAMING,
  FLOW_TYPE_CDC,
  FLOW_TYPE_RAG,
  FLOW_TYPE_ELT,
  FLOW_TYPE_CUSTOM,
  FLOW_TYPE_IN_MEMORY,
  FLOW_TYPE_REPLICATION,
  FLOW_TYPE_SPARK,
  FLOW_TYPE_API_SERVER,
} from 'js/constants/pipelineConstants';
import { toUIFlowType } from './flowTypeMapping';

// ============================================================================
// FLOW TYPE CATEGORIES
// ============================================================================

/**
 * Flow types that are always available to all users in any environment.
 * These require no feature flags or database checks.
 */
export const ALWAYS_AVAILABLE_FLOW_TYPES: (FlowType | string)[] = [
  'streaming',
  'cdc',
  'genai_rag', // Note: genai_rag is used but not in FlowType union yet
];

/**
 * Flow types controlled by GrowthBook feature flags for gradual rollout.
 * Once GA, these can be moved to ALWAYS_AVAILABLE_FLOW_TYPES.
 *
 * Format: Map of flow type to GrowthBook feature flag key
 */
export const GROWTHBOOK_ROLLOUT_FLOW_TYPES: Partial<Record<FlowType, string>> = {
  [FLOW_TYPE_API_SERVER]: 'UI_ENABLE_API_SERVER_FLOW',
  // Add more flow types here as needed for temporary rollout control
};

/**
 * Flow types controlled by database org flags (features_enabled).
 * These require the flow type to be in the org's features_enabled array.
 *
 * Format: Map of backend flow type to UI/feature name (used in org.features_enabled)
 *
 * IMPORTANT: Uses UI/feature names (directflow, byo, etc.) NOT backend flow_type names.
 * This matches what's stored in org.features_enabled array.
 *
 * For private installs, ORG_FEATURE_FLAG_SETTINGS controls behavior:
 * - check_limits (global flag): If false, all these flows are always available (bypasses DB check)
 * - If check_limits is true, these flows check org.features_enabled UNLESS they're in
 *   bypass_limits_for array, which allows selective bypass for specific flows.
 *
 * Example: { "check_limits": true, "bypass_limits_for": ["byo"] }
 *   → Most flows check DB, but "byo" (custom flow) is always enabled
 *
 * Note: bypass_limits_for uses UI/feature names (byo, directflow, etc.), not backend names.
 */
export const DB_ORG_SPECIFIC_FLOW_TYPES: Partial<Record<FlowType, string>> = {
  // Backend flow_type -> UI/feature name (for org.features_enabled)
  [FLOW_TYPE_IN_MEMORY]: 'directflow', // backend: in_memory -> UI: directflow
  [FLOW_TYPE_REPLICATION]: 'replication', // same for both
  [FLOW_TYPE_SPARK]: 'spark', // same for both
  [FLOW_TYPE_ELT]: 'elt', // same for both
  [FLOW_TYPE_CUSTOM]: 'byo', // backend: custom -> UI: byo
};

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface FlowTypeVisibilityConfig {
  /** Whether to check database limits (controlled by ORG_FEATURE_FLAG_SETTINGS.check_limits) */
  checkDatabaseLimits: boolean;
  /** List of flow types that bypass database checks even when checkDatabaseLimits is true */
  bypassLimitsFor: string[];
  /** Org's features_enabled array (from database) */
  orgFeaturesEnabled: string[];
  /** Function to check GrowthBook feature flag (returns boolean) */
  getGrowthBookFlag: (flagKey: string) => boolean;
}

export interface FlowTypeVisibilityResult {
  /** Whether the flow type is visible/enabled */
  isEnabled: boolean;
  /** Which control mechanism determined visibility */
  source: 'always_available' | 'growthbook' | 'db_check' | 'db_override';
}

// ============================================================================
// CORE LOGIC
// ============================================================================

/**
 * Checks if a flow type is available based on the configured control mechanisms.
 *
 * Evaluation order:
 * 1. Always available flows → always enabled
 * 2. GrowthBook rollout flows → check GrowthBook flag
 * 3. DB org-specific flows → check org features_enabled (if check_limits is true)
 *
 * @param flowType - The flow type to check
 * @param config - Visibility configuration
 * @returns Visibility result with isEnabled flag and source
 */
export function getFlowTypeVisibility(
  flowType: FlowType | string,
  config: FlowTypeVisibilityConfig,
): FlowTypeVisibilityResult {
  // 1. Always available flows
  if (ALWAYS_AVAILABLE_FLOW_TYPES.includes(flowType)) {
    return {
      isEnabled: true,
      source: 'always_available',
    };
  }

  // 2. GrowthBook rollout flows
  const growthbookFlagKey = GROWTHBOOK_ROLLOUT_FLOW_TYPES[flowType as FlowType];
  if (growthbookFlagKey) {
    const isEnabled = config.getGrowthBookFlag(growthbookFlagKey);
    return {
      isEnabled,
      source: 'growthbook',
    };
  }

  // 3. DB org-specific flows
  const dbFeatureName = DB_ORG_SPECIFIC_FLOW_TYPES[flowType as FlowType];
  if (dbFeatureName) {
    // Convert flow type to UI/feature name for bypass check
    const uiFlowType = toUIFlowType(flowType);

    // If check_limits is false, these flows are always available (for private installs)
    if (!config.checkDatabaseLimits) {
      return {
        isEnabled: true,
        source: 'db_override',
      };
    }

    // Check if this flow type is in the bypass list (even when check_limits is true)
    // bypass_limits_for uses UI/feature names (byo, directflow, etc.)
    if (config.bypassLimitsFor.includes(uiFlowType) || config.bypassLimitsFor.includes(flowType)) {
      return {
        isEnabled: true,
        source: 'db_override',
      };
    }

    // Check if the org has the required feature enabled
    // dbFeatureName is already the UI/feature name (directflow, byo, etc.)
    const isEnabled = config.orgFeaturesEnabled.includes(dbFeatureName);
    return {
      isEnabled,
      source: 'db_check',
    };
  }

  // Unknown flow type - default to disabled for safety
  console.warn(`Unknown flow type: ${flowType}. Defaulting to disabled.`);
  return {
    isEnabled: false,
    source: 'db_check',
  };
}

/**
 * Gets visibility for multiple flow types at once.
 * Useful for batch checking in components.
 *
 * @param flowTypes - Array of flow types to check
 * @param config - Visibility configuration
 * @returns Map of flow type to visibility result
 */
export function getFlowTypesVisibility(
  flowTypes: FlowType[],
  config: FlowTypeVisibilityConfig,
): Record<FlowType, FlowTypeVisibilityResult> {
  const result: Record<FlowType, FlowTypeVisibilityResult> = {} as any;

  for (const flowType of flowTypes) {
    result[flowType] = getFlowTypeVisibility(flowType, config);
  }

  return result;
}

// ============================================================================
// HELPER: Parse org features_enabled
// ============================================================================

/**
 * Parses org.features_enabled from various formats (string, array, null)
 * into a consistent array format.
 */
export function parseOrgFeaturesEnabled(
  featuresEnabled: string[] | string | null | undefined,
): string[] {
  if (!featuresEnabled) return [];
  if (typeof featuresEnabled === 'string') {
    try {
      return JSON.parse(featuresEnabled);
    } catch {
      return [];
    }
  }
  if (Array.isArray(featuresEnabled)) {
    return featuresEnabled;
  }
  return [];
}

// ============================================================================
// LEGACY COMPATIBILITY: Get conditionally enabled flow types object
// ============================================================================

/**
 * Legacy compatibility helper for components that expect the old format:
 * { [flowType]: boolean }
 *
 * @param config - Visibility configuration
 * @returns Object mapping flow types to enabled boolean
 */
export function getConditionallyEnabledFlowTypes(
  config: FlowTypeVisibilityConfig,
): Partial<Record<FlowType, boolean>> {
  const allDbFlowTypes = Object.keys(DB_ORG_SPECIFIC_FLOW_TYPES) as FlowType[];

  const result: Partial<Record<FlowType, boolean>> = {};

  for (const flowType of allDbFlowTypes) {
    const visibility = getFlowTypeVisibility(flowType, config);
    result[flowType] = visibility.isEnabled;
  }

  return result;
}
